<template>
  <!-- NOTE: 名称は汎用的だが、今は資料専用 -->
  <!-- サイドメニューが開いている状態 -->
  <div v-if="expand" v-cloak>
    <div class="link" @click="onClick">
      <span class="link-text">
        {{ text }}
      </span>
      <!-- 資料種別並び替えとかの編集テキスト -->
      <div class="edit-icon" v-if="!isEditing" :class="{ show: isOpen }">
        <playlist-edit-icon class="edit" :fillColor="iconColor" @click.stop="onEditIconClick" />
        <delete-icon class="edit" :fillColor="iconColor" @click.stop="onDeleteIconClick" />
      </div>
      <span class="edit-text" v-if="isEditing" :class="{ show: isOpen }" @click.stop="onCancelClick">
        {{ $t("common.cancel") }}
      </span>
    </div>

    <!-- 開閉するコンテンツ -->
    <expanded-item :isOpen="isOpen">
      <div class="material-container">
        <sortable-list
          :items.sync="reorderableListItems"
          keyAttr="id"
          :disabled="!isEditing"
          :onReorder="onReorder"
          handleClassName=".drag-icon"
          v-slot:default="{ item }"
        >
          <div class="link-shrink" :class="{ active: isActive(item) }" @click="onLinkClick(item)">
            <div v-if="showDeleteCheckbox(item)" class="sidemenu-icon-shrink delete">
              <checkbox-marked-outline-icon
                class="center pointer"
                v-if="checkedItems(item)"
                :size="20"
                @click.stop="onCheck(item)"
              />
              <checkbox-blank-outline-icon class="center pointer" v-else :size="20" @click.stop="onCheck(item)" />
            </div>
            <img
              v-else
              class="sidemenu-icon-shrink"
              :src="item.icon"
              :alt="item.text"
              @click="onDeleteIconClick(item)"
            />
            <input
              v-if="showTextInput(item)"
              type="text"
              class="link-text link-text-shrink"
              :id="item.id"
              :value="item.text"
              maxlength="20"
              @input="onInput"
            />
            <span v-else class="link-text link-text-shrink">{{ item.text }}</span>
            <img class="drag-icon" :class="{ edit: isListEditing }" src="@/assets/img/icon/drag.png" />
          </div>
        </sortable-list>
        <div class="link-shrink template-link" :class="{ active: isActive(themeItem) }" @click="onLinkClick(themeItem)">
          <img class="sidemenu-icon-shrink" :src="themeItem.icon" :alt="themeItem.text" />
          <div class="template-link-label">
            <span class="link-text link-text-shrink">{{ themeItem.text }}</span>
            <span
              v-if="isFree"
              class="template-premium-badge"
              @mouseenter="showTemplateTooltip"
              @mouseleave="hideTemplateTooltip"
            >
              <img src="@/assets/img/icon/premium.png" alt="Premium" />
            </span>
          </div>
        </div>
        <div
          v-if="!isEditing"
          class="add-text center"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.addNewAttributeAlt')"
          @click.stop="onAddButtonClick"
        >
          {{ $t("materialPage.sideMenuExpandEditableLink.addNewAttribute") }}
        </div>
        <div
          v-if="showUpdateButton"
          class="add-text center"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.updateAttributesAlt')"
          @click.stop="onUpdateButtonClick"
        >
          <playlist-edit-icon class="center" :size="20" />
          {{ $t("materialPage.sideMenuExpandEditableLink.saveChanges") }}
        </div>
        <div
          v-if="showDeleteButton"
          class="add-text center delete"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.deleteAttributesAlt')"
          @click.stop="onDeleteButtonClick"
        >
          <delete-icon class="center" :size="20" />
          <span class="center">{{
            $t("materialPage.sideMenuExpandEditableLink.deleteCount", { count: selectedItems.length })
          }}</span>
        </div>
      </div>
    </expanded-item>
    <div
      v-if="isFree && isTemplateTooltipVisible"
      class="template-premium-tooltip"
      :style="{ top: templateTooltipPosition.top + 'px', left: templateTooltipPosition.left + 'px' }"
    >
      {{ $t("exportManuscriptDialog.premiumOnly") }}
    </div>
  </div>

  <!-- サイドメニューが閉じている状態 -->
  <div v-else class="closed" @mouseover="onMouseOver" @mouseleave="onMouseLeave">
    <div class="link" :class="{ shrink: true, active: isCurrent }" @click="() => onLinkClick()">
      <img class="sidemenu-icon" :class="{ shrink: true }" :src="icon" :alt="text" />
    </div>
    <div v-if="isMouseHover" class="float-menu">
      <div class="header">
        <span class="text">{{ text }}</span>
        <div class="edit-icon" v-if="!isEditing" :class="{ show: isOpen }">
          <playlist-edit-icon class="edit" :fillColor="iconColor" @click.stop="onEditIconClick" />
          <delete-icon class="edit" :fillColor="iconColor" @click.stop="onDeleteIconClick" />
        </div>
        <span class="edit-text" v-if="isEditing" :class="{ show: isOpen }" @click.stop="onCancelClick">
          {{ $t("common.cancel") }}
        </span>
      </div>
      <div class="material-container">
        <sortable-list
          :items.sync="reorderableListItems"
          keyAttr="id"
          :disabled="!isEditing"
          :onReorder="onReorder"
          handleClassName=".drag-icon"
          v-slot:default="{ item }"
        >
          <div class="link-shrink float-menu-item" @click="onLinkClick(item)">
            <div v-if="showDeleteCheckbox(item)" class="sidemenu-icon-shrink delete">
              <checkbox-marked-outline-icon
                class="center pointer"
                v-if="checkedItems(item)"
                :size="20"
                @click.stop="onCheck(item)"
              />
              <checkbox-blank-outline-icon class="center pointer" v-else :size="20" @click.stop="onCheck(item)" />
            </div>
            <img
              v-else
              class="sidemenu-icon-shrink"
              :src="item.icon"
              :alt="item.text"
              @click="onDeleteIconClick(item)"
            />
            <input
              v-if="showTextInput(item)"
              type="text"
              class="link-text link-text-shrink"
              :id="item.id"
              :value="item.text"
              maxlength="20"
              @input="onInput"
            />
            <span v-else class="link-text link-text-shrink item-text">{{ item.text }}</span>
            <img class="drag-icon" :class="{ edit: isListEditing }" src="@/assets/img/icon/drag.png" />
          </div>
        </sortable-list>
        <div
          class="link-shrink float-menu-item template-link"
          :class="{ active: isActive(themeItem) }"
          @click="onLinkClick(themeItem)"
        >
          <img class="sidemenu-icon-shrink" :src="themeItem.icon" :alt="themeItem.text" />
          <div class="template-link-label">
            <span class="link-text link-text-shrink item-text">{{ themeItem.text }}</span>
            <span
              v-if="isFree"
              class="template-premium-badge"
              @mouseenter="showTemplateTooltip"
              @mouseleave="hideTemplateTooltip"
            >
              <img src="@/assets/img/icon/premium.png" alt="Premium" />
            </span>
          </div>
        </div>
        <div
          v-if="!isEditing"
          class="add-text center float-menu-item"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.addNewAttributeAlt')"
          @click.stop="onAddButtonClick"
        >
          {{ $t("materialPage.sideMenuExpandEditableLink.addNewAttribute") }}
        </div>
        <div
          v-if="showUpdateButton"
          class="add-text center float-menu-item"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.updateAttributesAlt')"
          @click.stop="onUpdateButtonClick"
        >
          <playlist-edit-icon class="center" :size="20" />
          {{ $t("materialPage.sideMenuExpandEditableLink.saveChanges") }}
        </div>
        <div
          v-if="showDeleteButton"
          class="add-text center delete float-menu-item"
          :class="{ show: isOpen }"
          :alt="$t('materialPage.sideMenuExpandEditableLink.deleteAttributesAlt')"
          @click.stop="onDeleteButtonClick"
        >
          <delete-icon class="center" :size="20" />
          <span class="center">{{
            $t("materialPage.sideMenuExpandEditableLink.deleteCount", { count: selectedItems.length })
          }}</span>
        </div>
      </div>
    </div>
    <div
      v-if="isFree && isTemplateTooltipVisible"
      class="template-premium-tooltip"
      :style="{ top: templateTooltipPosition.top + 'px', left: templateTooltipPosition.left + 'px' }"
    >
      {{ $t("exportManuscriptDialog.premiumOnly") }}
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Route } from "vue-router";
import ExpandedItem from "@/components/atoms/ExpandedItem.vue";
import SortableList from "@/components/molecules/SortableList.vue";
import { ListState, MaterialAttribute, Plan } from "@/lib/models";
import { Dialog, isBilling, reOrderData } from "@/lib/utils";
import { SortOrder } from "@/lib/models/sortOrder";
import MaterialAttributeEditDialog, {
  MaterialAttributeEditDialogProps,
} from "@/components/ui/MaterialAttributeEditDialog.vue";
import PlaylistEditIcon from "icons/PlaylistEdit.vue";
import DeleteIcon from "icons/Delete.vue";
import CheckboxBlankOutlineIcon from "icons/CheckboxBlankOutline.vue";
import CheckboxMarkedOutlineIcon from "icons/CheckboxMarkedOutline.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/SimpleDialog.vue";
import DialogVue from "@/components/ui/Dialog.vue";

interface Data {
  isOpen: boolean;
  isCurrent: boolean;
  currentListState: ListState;
  currentName: string;
  removeIcon: unknown;
  reorderableListItems: MaterialTileModel[];
  selectedItems: MaterialTileModel[];
  isMouseHover: boolean;
  themeWatcher: MutationObserver | null;
  isTemplateTooltipVisible: boolean;
  templateTooltipPosition: { top: number; left: number };
}

interface Methods {
  initialize: () => void;
  isActive: (item: MaterialTileModel) => boolean;
  showDeleteCheckbox: (item: MaterialTileModel) => boolean;
  showTextInput: (item: MaterialTileModel) => boolean;
  showErrorDialog: (message: string) => void;
  onInput: (event: InputEvent) => void;
  onClick: () => void;
  onLinkClick: (item?: MaterialTileModel) => void;
  onAddButtonClick: () => void;
  onEditIconClick: (event: PointerEvent) => void;
  onDeleteIconClick: (event: PointerEvent) => void;
  onCancelClick: (event: PointerEvent) => void;
  onReorder: (items: MaterialTileModel[]) => void;
  onCheck: (item: MaterialTileModel) => void;
  onUpdateButtonClick: () => void;
  onDeleteButtonClick: () => void;
  addMaterialAttribute: (value: string) => void;
  updateMaterialAttributes: () => void;
  deleteMaterialAttributes: () => void;
  checkedItems: (item: MaterialTileModel) => boolean;
  convertTileModel: (items: MaterialAttribute[]) => MaterialTileModel[];
  pushPage: (item: MaterialTileModel) => void;
  onMouseOver: () => void;
  onMouseLeave: () => void;
  showTemplateTooltip: (event: MouseEvent) => void;
  hideTemplateTooltip: () => void;
}

interface Computed {
  listItems: MaterialTileModel[];
  themeItem: MaterialTileModel;
  isListEditing: boolean;
  isDeleteEditing: boolean;
  isEditing: boolean;
  showUpdateButton: boolean;
  showDeleteButton: boolean;
  iconColor: string;
  isFree: boolean;
}

interface Props {
  link: string;
  text: string;
  icon: string;
  novelId: string;
  expand: boolean;
}

type MaterialTileModel = {
  id: string;
  text: string;
  icon: string;
  link: string;
};

const characterIcon = require("@/assets/icons/figma/Profile.svg");
const worldViewIcon = require("@/assets/icons/figma/MapPinned.svg");
const correlationIcon = require("@/assets/icons/figma/Diagram.svg");
const materialIcon = require("@/assets/img/menu-icon/material.png");
const removeIcon = require("@/assets/img/icon/remove.png");
const templateIcon = require("@/assets/img/menu-icon/template.png");

const defaultMaterials = ["characters", "worldViews", "correlations"];

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ExpandedItem,
    SortableList,
    PlaylistEditIcon,
    DeleteIcon,
    CheckboxBlankOutlineIcon,
    CheckboxMarkedOutlineIcon,
  },
  data() {
    return {
      isOpen: true,
      isCurrent: this.$route.name === this.link,
      currentListState: ListState.COMPLETE,
      currentName: "",
      removeIcon,
      reorderableListItems: [],
      selectedItems: [],
      isMouseHover: false,
      themeWatcher: null as MutationObserver | null,
      isTemplateTooltipVisible: false,
      templateTooltipPosition: { top: 0, left: 0 },
    };
  },
  created() {
    const { initialize } = this;
    initialize();

    // テーマ変更を検知するMutationObserverを設定
    if (typeof window !== "undefined") {
      this.themeWatcher = new MutationObserver(() => {
        this.$forceUpdate();
      });
      this.themeWatcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-app-theme"],
      });
    }
  },
  beforeDestroy() {
    if (this.themeWatcher) {
      this.themeWatcher.disconnect();
      this.themeWatcher = null;
    }
  },
  props: {
    link: String,
    text: String,
    icon: String,
    novelId: String,
    expand: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    isListEditing() {
      const { currentListState } = this;
      return currentListState === ListState.EDIT;
    },
    isDeleteEditing() {
      const { currentListState } = this;
      return currentListState === ListState.DELETE;
    },
    isEditing() {
      const { isListEditing, isDeleteEditing } = this;
      return isListEditing || isDeleteEditing;
    },
    listItems() {
      const { novelId, convertTileModel, selectedItems } = this;
      const attributes = this.$store.getters["materialModule/attributesFromNovelId"](novelId) as MaterialAttribute[];
      const sortOrder = this.$store.getters["materialModule/attributeSortOrder"](novelId) as SortOrder | undefined;
      let data = attributes;
      if (sortOrder) {
        data = reOrderData(attributes, sortOrder.order);
      }
      const items: MaterialTileModel[] = convertTileModel(data);
      return items.filter((item) => !selectedItems.some((remove) => remove.id === item.id));
    },
    themeItem() {
      return {
        id: "",
        text: this.$t("materialPage.sideMenuExpandEditableLink.materialTemplate") as string,
        icon: templateIcon,
        link: "templates",
      };
    },
    showUpdateButton() {
      const { isListEditing } = this;
      return isListEditing;
    },
    showDeleteButton() {
      const { isDeleteEditing, selectedItems } = this;
      return isDeleteEditing && selectedItems.length > 0;
    },
    iconColor() {
      if (typeof window !== "undefined") {
        const theme = document.documentElement.getAttribute("data-app-theme");
        return theme === "dark" ? "#e0e0e0" : "#474a4d";
      }
      return "#474a4d";
    },
    isFree() {
      return this.$store.getters["billingModule/plan"] === Plan.free;
    },
  },
  methods: {
    // init

    async initialize() {
      const { $route, $store } = this;
      const { params } = $route;
      const { dispatch } = $store;
      await dispatch("materialModule/initialize", params.novelId);
      this.selectedItems = [];
      this.reorderableListItems = this.listItems;
      this.currentListState = ListState.COMPLETE;
    },

    // value

    isActive(item: MaterialTileModel) {
      const { id, link } = item;
      if (defaultMaterials.includes(link)) {
        return this.currentName === link;
      }

      const { name, params } = this.$route;
      return name === link && params.materialAttributeId === id;
    },

    // util

    showDeleteCheckbox(item) {
      const { isDeleteEditing } = this;
      const { link } = item;

      return isDeleteEditing && link === "materials";
    },
    showTextInput(item) {
      const { isListEditing } = this;
      const { link } = item;

      return isListEditing && link === "materials";
    },
    showErrorDialog(message: string) {
      const confirmDialog = new Dialog(DialogVue);
      const options = {
        title: this.$t("materialPage.sideMenuExpandEditableLink.errorTitle") as string,
        content: message,
      };

      confirmDialog.show(options);
    },
    convertTileModel(items: MaterialAttribute[]) {
      const tiles = items.map((item) => {
        const tile = {
          id: item.id,
          text: item.name,
          icon: materialIcon,
          link: "materials",
        };

        if (item.id === "character") {
          return {
            ...tile,
            icon: characterIcon,
            link: "characters",
          };
        }

        if (item.id === "worldview") {
          return {
            ...tile,
            icon: worldViewIcon,
            link: "worldViews",
          };
        }

        if (item.id === "correlation") {
          return {
            ...tile,
            icon: correlationIcon,
            link: "correlations",
          };
        }

        return tile;
      });

      return tiles;
    },
    pushPage(item) {
      const { novelId } = this;
      const { isActive } = this;

      if (isActive(item)) return;

      if (defaultMaterials.includes(item.link)) {
        this.$router.push({ name: item.link, params: { novelId } });
        return;
      }

      this.$router.push({
        name: item.link,
        params: { novelId, materialAttributeId: item.id },
      });
    },

    // event handle

    onInput(event: InputEvent) {
      if (!(event.target instanceof HTMLInputElement)) {
        return;
      }

      if (!event.target) {
        return;
      }

      const { reorderableListItems } = this;
      const { value, id } = event.target;

      this.reorderableListItems = reorderableListItems.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            text: value,
          };
        }

        return item;
      });
    },
    onClick() {
      this.isOpen = !this.isOpen;
      const { initialize } = this;
      initialize();
    },
    onLinkClick(item?: MaterialTileModel) {
      const { isEditing, isCurrent, pushPage, listItems } = this;

      if (isEditing) return;

      if (!item && !isCurrent) {
        const firstItem = listItems[0];
        pushPage(firstItem);
        return;
      }

      if (!item) {
        return;
      }

      pushPage(item);
    },
    async onAddButtonClick() {
      const { $store, $router } = this;
      const billing = await isBilling($store);
      if (!billing) {
        $router.push({ name: "subscriptionAnnounce", query: { from: "createMaterialAttribute" } });
        return;
      }

      const { addMaterialAttribute } = this;
      const addAttributeDialog = new Dialog(MaterialAttributeEditDialog);
      const data: MaterialAttributeEditDialogProps = {
        title: this.$t("materialPage.sideMenuExpandEditableLink.createAttributeTitle") as string,
        content: this.$t("materialPage.sideMenuExpandEditableLink.createAttributeContent") as string,
        input: "",
        placeholder: this.$t("materialPage.sideMenuExpandEditableLink.attributeNamePlaceholder") as string,
        positiveCallback: addMaterialAttribute,
      };
      addAttributeDialog.show(data);
    },
    onEditIconClick(_: PointerEvent) {
      this.currentListState = ListState.EDIT;
    },
    onDeleteIconClick(_: PointerEvent) {
      this.currentListState = ListState.DELETE;
    },
    onCancelClick(_) {
      const { initialize } = this;
      initialize();
    },
    onReorder(items: MaterialTileModel[]) {
      this.reorderableListItems = items;
    },
    onCheck(item: MaterialTileModel) {
      const { selectedItems } = this;
      if (selectedItems.includes(item)) {
        this.selectedItems = selectedItems.filter((selectedItem) => selectedItem.id !== item.id);
        return;
      }

      selectedItems.push(item);
    },
    onUpdateButtonClick() {
      const { reorderableListItems, updateMaterialAttributes } = this;

      for (let index = 0; index < reorderableListItems.length; index += 1) {
        const item = reorderableListItems[index];
        if (item.text.length < 1) {
          const { showErrorDialog } = this;
          showErrorDialog(this.$t("materialPage.sideMenuExpandEditableLink.emptyAttributeError") as string);
          return;
        }
      }

      updateMaterialAttributes();
    },
    onDeleteButtonClick() {
      const { initialize, selectedItems, deleteMaterialAttributes } = this;
      if (selectedItems.length < 1) {
        initialize();
        return;
      }

      const confirmDialog = new Dialog(SimpleDialog);
      const data: SimpleDialogProps = {
        title: this.$t("materialPage.sideMenuExpandEditableLink.deleteAttributeTitle") as string,
        content: this.$t("materialPage.sideMenuExpandEditableLink.deleteAttributeContent") as string,
        positive: this.$t("materialPage.sideMenuExpandEditableLink.deleteButton") as string,
        positiveCallback: deleteMaterialAttributes,
      };
      confirmDialog.show(data);
    },

    // update value (or execute action)

    async addMaterialAttribute(value: string) {
      const { initialize, novelId, $store } = this;
      const { dispatch } = $store;
      const payload = {
        novelId,
        name: value,
      };
      await dispatch("materialModule/createMaterialAttribute", payload);

      initialize();
    },
    async updateMaterialAttributes() {
      const { initialize, reorderableListItems, novelId, listItems, $store } = this;
      const { dispatch, getters } = $store;
      const preOrder = listItems.map((item) => item.id);
      const order = reorderableListItems.map((item) => item.id);
      if (preOrder !== order) await dispatch("materialModule/updateMaterialAttributeOrder", { novelId, order });

      let attributes = getters["materialModule/attributesFromNovelId"](novelId) as MaterialAttribute[];
      attributes = attributes.map((attribute) => {
        const item = reorderableListItems.find((item) => item.id === attribute.id);
        if (!item) {
          return attribute;
        }

        if (item.text === attribute.name) {
          return attribute;
        }

        return {
          ...attribute,
          name: item.text,
        };
      });
      await dispatch("materialModule/updateMaterialAttribute", attributes);

      initialize();
    },
    async deleteMaterialAttributes() {
      const { initialize, selectedItems, $store, $route } = this;
      const { dispatch } = $store;
      const deleteIds = selectedItems.map((item) => item.id);

      await dispatch("materialModule/deleteMaterialAttribute", deleteIds);
      initialize();

      const { reorderableListItems, pushPage } = this;
      const { name, params } = $route;
      if (name && deleteIds.includes(params.materialAttributeId)) {
        const first = reorderableListItems[0];
        pushPage(first);
      }
    },
    checkedItems(item) {
      const { selectedItems } = this;
      return selectedItems.includes(item);
    },
    onMouseOver() {
      this.isMouseHover = true;
    },
    onMouseLeave() {
      this.isMouseHover = false;
    },
    showTemplateTooltip(event: MouseEvent) {
      const target = event.currentTarget as HTMLElement | null;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      this.templateTooltipPosition = {
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      };
      this.isTemplateTooltipVisible = true;
    },
    hideTemplateTooltip() {
      this.isTemplateTooltipVisible = false;
    },
  },
  watch: {
    $route(to: Route, from: Route) {
      if (to.name) {
        this.isCurrent = ["materials", "characters", "worldViews", "correlations"].includes(to.name);
        this.currentName = to.name;
        return;
      }

      this.isCurrent = false;
      this.currentName = "";
      this.isEditing = false;
      this.selectedItems = [];
    },
    listItems(to, from) {},
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.link {
  position: relative;
  @include hover-overlay;
  display: grid;
  align-items: center;
  grid-template-columns: 26px 12px auto 8px 1fr auto;
  margin: auto;
  padding: 12px 16px 12px 16px;
  text-align: left;
  text-decoration: none;
  font-size: var(--font-size-md);
  font-family: "Courier New", Courier, monospace;
  color: var(--color-text-normal);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.shrink {
    grid-template-columns: 1fr;
    place-items: center;
    padding: 16px 8px 16px 8px;
  }

  &-shrink {
    position: relative;
    @include hover-overlay;
    display: grid;
    align-items: center;
    grid-template-columns: 20px 1fr 20px;
    padding-left: 24px;
    padding-right: 16px;
    padding-top: 8px;
    padding-bottom: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &.active {
      background-color: var(--color-surface-subtle);
      text-decoration: none;
    }

    &:hover {
      text-decoration: none;
    }
  }

  &.active {
    background-color: var(--color-surface-subtle);
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
  }

  &:visited {
    color: var(--color-text-normal);
    font-weight: var(--font-weight-bold);
  }
}

.link-text {
  grid-column: 1/3;
  word-break: break-word;
  line-height: initial;
  color: var(--color-text-normal);

  &-shrink {
    grid-column: 2/2;
    padding-left: 8px;
    padding-right: 8px;
    max-height: 30px;
    cursor: default;
  }
}

input {
  margin-right: 0.5rem;
  margin-left: 0.5rem;
  cursor: auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  padding: 4px;
}

.drag-icon {
  display: none;
  grid-column: 3/3;
  width: 100%;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }

  &.edit {
    display: block;
  }
}

.edit-text {
  display: none;
  grid-column: 6/6;
  font-size: var(--font-size-xs);
  cursor: pointer;
  color: var(--color-inactive);
  line-height: initial;

  &.show {
    display: block;
  }
}

.add-text {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: initial;
  color: var(--color-primary);
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 24px;
  padding-right: 16px;
  cursor: pointer;

  &.delete {
    color: var(--color-warning);
  }
}

.sidemenu-icon {
  grid-column: 1/2;
  width: 100%;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }

  &.shrink {
    grid-column: 1/3;
    width: 30px;
  }

  &-shrink {
    height: 20px;
    filter: brightness(0);

    [data-app-theme="dark"] & {
      filter: brightness(0) invert(1);
    }
  }
}

.edit-icon {
  display: none;
  flex-direction: row;
  grid-column: 6/6;

  &.show {
    display: flex;
  }
}

.edit {
  cursor: pointer;
  display: flex;
  justify-content: center;
}

.center {
  display: flex;
  justify-content: center;
}

.pointer {
  cursor: pointer;
}

.template-link-label {
  display: flex;
  align-items: center;
  gap: 4px;
  grid-column: 2 / 3;
  min-width: 0;

  .link-text-shrink {
    min-width: 0;
    flex-shrink: 1;
  }
}

.template-premium-badge {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  cursor: default;

  img {
    width: 18px;
    height: 18px;
    filter: none;
  }
}

.template-premium-tooltip {
  position: fixed;
  transform: translateX(-30%);
  z-index: 10;
  @include premium-tooltip;
}

.closed {
  position: relative;

  .float-menu {
    position: fixed;
    margin-left: 60px;
    margin-top: -62px;
    background-color: var(--color-surface-subtle);
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    cursor: default;
    box-shadow: var(--shadow-md);

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
      padding: 0.5em;
      font-size: 1.1em;
      color: var(--color-text-normal);

      .text {
        font-weight: var(--font-weight-bold);
      }
    }

    .material-container {
      .float-menu-item {
        position: relative;
        @include hover-overlay;
        box-sizing: border-box;
        padding: 0.5em;
        color: var(--color-text-normal);
        cursor: pointer;
        transition: background-color 0.2s ease;

        .item-text {
          text-align: left;
        }

      }
    }
  }
}
</style>
